import { useEffect, useState } from 'react';

import { clock } from './clock';
import { CreatureFace } from './CreatureFace';
import type { Expression } from './expression';
import { faceFor, type Rig } from './faces';
import { HumanFace } from './HumanFace';
import { UnicornFace } from './UnicornFace';

const RIGS: Record<Rig, typeof HumanFace> = {
  human: HumanFace,
  creature: CreatureFace,
  unicorn: UnicornFace,
};

/**
 * The shared clock's timestamp, as React state.
 *
 * A hook rather than something `CharacterFace` does for itself, because two
 * callers want the phase at different granularities. `TutorAvatar` needs it to
 * decide *which* expression to draw — `heldExpression` measures how stale a
 * reaction cue is against it — so the value has to exist before the face is
 * rendered. The select screen wants one subscription for a grid of nine faces,
 * not nine, and gets that by calling this once and passing the result down.
 *
 * The clock is one loop for the whole app either way (§8.4); this only governs
 * how many React re-renders a frame costs.
 */
export function usePhase(): number {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    // No clock in a test render or any future SSR; the face simply does not
    // move, which is the same thing reduced motion asks for.
    if (!clock) return;
    return clock.subscribe((t) => setPhase(t));
  }, []);
  return phase;
}

/**
 * One character's face, drawn at a given expression. No text, no live region.
 *
 * One interface over every rig. Callers pass the same props whichever is
 * behind it, which is the promise Q1 makes — and what lets commissioned art
 * replace any renderer later without touching a call site.
 *
 * Split out of `TutorAvatar` when the select screen wanted faces too. The
 * boundary is exactly the live region: `TutorAvatar` announces the turn state
 * for a screen reader, and nine of those on the select screen — each saying
 * "Waiting" — is the failure its own comment warns about. A grid of faces
 * needs the drawing and none of the announcing.
 */
export function CharacterFace({
  artKey,
  accentHex,
  intensity,
  expression,
  phase,
}: {
  artKey: string;
  accentHex: string;
  intensity: number;
  expression: Expression;
  /** From `usePhase`. Passed in so one subscription can drive many faces. */
  phase: number;
}) {
  const face = faceFor(artKey);
  // A lookup rather than a ternary: a third rig is where nested conditionals
  // start, and `Record<Rig, …>` makes a fourth one a compile error until it
  // has a renderer.
  const Rig = RIGS[face.rig];
  const scale = clock?.intensityScale() ?? 1;

  return (
    // A block-display `span`, not a `div`: the select screen puts this inside
    // the card's choose button, and a button's content model is phrasing
    // content. Browsers tolerate a `div` there; the validator does not, and
    // this costs one class.
    <span
      className="block aspect-square w-full"
      // Both per-character values arrive as custom properties, never as
      // interpolated class names — Tailwind cannot see those (§9.2).
      // `intensity` is the character's own dial; `scale` is the clock backing
      // off under load (§8.4).
      style={
        {
          '--tutor-accent': accentHex,
          '--tutor-intensity': intensity * scale,
        } as React.CSSProperties
      }
    >
      <Rig face={face} expression={expression} phase={phase} />
    </span>
  );
}
